/**
 * Composant partagé pour le challenge MFA (phase 2 d'une authentification).
 *
 * Utilisé par :
 *   - `LoginPage` : après le step "credentials" si l'user a MFA activée.
 *   - `MagicLinkPage` : après vérification du token email si MFA activée.
 *
 * Le composant ne sait pas d'où vient le `pre_mfa_token` : il reçoit le token,
 * gère la saisie code TOTP / recovery + l'appel backend, puis délègue la
 * navigation post-succès au parent via `onSuccess(result)`.
 *
 * API minimale, pas de routing interne, pas de manipulation de cache. Garde
 * la même UX entre login et magic link (cohérence visuelle pour l'utilisateur
 * qui voit toujours le même écran TOTP, peu importe son chemin d'arrivée).
 */

import { extractTrpcCode, NETWORK_ERROR_MESSAGE } from "@champ-erp/auth-primitives/web";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { MUTATION_SANS_RECUPERATION_SESSION_META } from "../../auth/mutation-session-recovery.js";
import { AuthLayout } from "../../layouts/AuthLayout.js";
import { trpc } from "../../lib/trpc.js";

interface MfaFormValues {
  code: string;
}

/**
 * Forme du résultat exposé côté frontend par les routes `auth.verifyMfaCode`
 * et `auth.useMfaRecoveryCode`. Le `session_token` est volontairement absent :
 * il est posé en cookie HttpOnly côté backend, jamais lu en JS.
 */
export interface MfaPromptSuccessResult {
  ok: true;
  domaine_id_actif: string | null;
}

export interface MfaCodePromptProps {
  /** Token pré-MFA reçu en phase 1 (login ou magic link), TTL 5 min côté backend. */
  preMfaToken: string;
  /** Appelé après une vérification MFA réussie. Le parent gère la navigation. */
  onSuccess: (result: MfaPromptSuccessResult) => void | Promise<void>;
  /**
   * Appelé quand l'utilisateur veut revenir au step précédent (bouton "Retour"
   * ou auto-bascule si le pré-MFA a expiré).
   */
  onBack: () => void;
  /** Label du lien retour (défaut "← Retour à la connexion"). */
  backLabel?: string;
}

export function MfaCodePrompt({
  preMfaToken,
  onSuccess,
  onBack,
  backLabel = "← Retour à la connexion",
}: MfaCodePromptProps) {
  // Toggle entre code TOTP (6 chiffres) et code de récupération (16 hex
  // groupés "xxxx-xxxx-xxxx-xxxx"). Stocké localement, pas de persistance.
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  // Pas de zodResolver : le format diffère entre TOTP (6 chiffres) et recovery
  // (32 caractères hex). Les schémas du contracts sont déjà appliqués côté
  // backend qui répondra "Code invalide" si le format ne convient pas.
  const form = useForm<MfaFormValues>({
    defaultValues: { code: "" },
    mode: "onSubmit",
  });

  const verifyMutation = useMutation({
    meta: MUTATION_SANS_RECUPERATION_SESSION_META,
    mutationFn: (input: { pre_mfa_token: string; code: string; isRecovery: boolean }) => {
      if (input.isRecovery) {
        return trpc.auth.useMfaRecoveryCode.mutate({
          pre_mfa_token: input.pre_mfa_token,
          code: input.code,
        });
      }
      return trpc.auth.verifyMfaCode.mutate({
        pre_mfa_token: input.pre_mfa_token,
        code: input.code,
      });
    },
    onSuccess: async (result) => {
      setGlobalError(null);
      await onSuccess(result);
    },
    onError: (error) => {
      const code = extractTrpcCode(error);
      if (code === "UNAUTHORIZED") {
        // Le pre_mfa_token a expiré (TTL 5 min côté backend) → on bascule
        // automatiquement vers l'étape précédente après un court délai pour
        // que l'utilisateur ait le temps de lire le message.
        setGlobalError("Session expirée. Veuillez recommencer la connexion depuis le début.");
        window.setTimeout(() => {
          onBack();
        }, 2500);
      } else if (code === "BAD_REQUEST") {
        setGlobalError("Code invalide. Vérifiez votre application d'authentification.");
      } else {
        setGlobalError(NETWORK_ERROR_MESSAGE);
      }
    },
  });

  const onSubmit = (values: MfaFormValues) => {
    setGlobalError(null);
    verifyMutation.mutate({
      pre_mfa_token: preMfaToken,
      code: values.code.trim().replace(/\s+/g, ""),
      isRecovery: useRecoveryCode,
    });
  };

  const codeError = form.formState.errors.code?.message;

  return (
    <AuthLayout
      title="Vérification en deux étapes"
      subtitle={
        useRecoveryCode
          ? "Saisissez l'un de vos codes de récupération."
          : "Saisissez le code à 6 chiffres de votre application d'authentification."
      }
      footer={
        <button
          type="button"
          onClick={onBack}
          className="text-champagne-700 underline-offset-2 hover:text-champagne-900 hover:underline"
        >
          {backLabel}
        </button>
      }
    >
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5" noValidate>
        <div>
          <label htmlFor="mfa-code" className="mb-1.5 block text-sm font-medium text-champagne-800">
            {useRecoveryCode ? "Code de récupération" : "Code à 6 chiffres"}
          </label>
          <input
            id="mfa-code"
            type="text"
            inputMode={useRecoveryCode ? "text" : "numeric"}
            autoComplete="one-time-code"
            autoFocus
            placeholder={useRecoveryCode ? "xxxx-xxxx-xxxx-xxxx" : "000000"}
            maxLength={useRecoveryCode ? 32 : 6}
            aria-invalid={codeError || globalError ? "true" : "false"}
            {...form.register("code", { required: "Code requis." })}
            className="block h-12 w-full rounded-xl border border-champagne-800/20 bg-white px-4 text-base tracking-[0.18em] text-champagne-900 placeholder:text-champagne-800/30 focus:border-champagne-500 focus:outline-none focus:ring-2 focus:ring-champagne-500/20"
          />
          {codeError && (
            <p role="alert" className="mt-1.5 text-xs font-medium text-red-700">
              {codeError}
            </p>
          )}
        </div>

        {globalError && (
          <p
            role="alert"
            aria-live="polite"
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800"
          >
            {globalError}
          </p>
        )}

        <button
          type="submit"
          disabled={verifyMutation.isPending}
          className="cta-flat inline-flex h-12 w-full items-center justify-center rounded-xl text-[15px] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {verifyMutation.isPending ? "Vérification…" : "Vérifier le code"}
        </button>

        <button
          type="button"
          onClick={() => {
            setUseRecoveryCode((prev) => !prev);
            form.reset({ code: "" });
            setGlobalError(null);
          }}
          className="block w-full text-center text-sm text-champagne-700 underline-offset-2 hover:text-champagne-900 hover:underline"
        >
          {useRecoveryCode
            ? "← Utiliser plutôt mon application"
            : "Utiliser un code de récupération"}
        </button>
      </form>
    </AuthLayout>
  );
}
